import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const OwnerDashboard = () => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    monthlyRevenue: 0,
    occupancyRate: 0,
    maintenanceCosts: 0,
    totalUnits: 0,
    occupiedUnits: 0,
    vacantUnits: 0,
    leaseRenewals: 0,
    maintenanceRequests: 0
  });

  const fetchDashboardData = useCallback(async (tenantId) => {
    try {
      // Get properties and units
      const { data: properties } = await supabase
        .from('properties')
        .select(`
          *,
          units(*)
        `)
        .eq('tenant_id', tenantId);

      const totalUnits = properties?.reduce((sum, prop) => sum + (prop.units?.length || 0), 0) || 0;
      const occupiedUnits = properties?.reduce((sum, prop) => 
        sum + (prop.units?.filter(unit => unit.status === 'occupied').length || 0), 0) || 0;
      const vacantUnits = totalUnits - occupiedUnits;
      const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

      // Get monthly revenue from rent payments
      const currentMonth = new Date();
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const { data: rentPayments } = await supabase
        .from('rent_payments')
        .select('amount')
        .eq('status', 'paid')
        .gte('paid_date', startOfMonth.toISOString().split('T')[0])
        .lte('paid_date', endOfMonth.toISOString().split('T')[0]);
      
      const monthlyRevenue = rentPayments?.reduce((sum, payment) => sum + parseFloat(payment.amount), 0) || 0;

      // Get maintenance costs
      const { data: maintenanceRequests } = await supabase
        .from('maintenance_requests')
        .select('actual_cost')
        .not('actual_cost', 'is', null)
        .gte('completed_date', startOfMonth.toISOString().split('T')[0])
        .lte('completed_date', endOfMonth.toISOString().split('T')[0]);
      
      const maintenanceCosts = maintenanceRequests?.reduce((sum, req) => sum + parseFloat(req.actual_cost || 0), 0) || 0;

      // Get lease renewals (leases ending in next 60 days)
      const next60Days = new Date();
      next60Days.setDate(next60Days.getDate() + 60);
      
      const { data: renewals } = await supabase
        .from('leases')
        .select('id')
        .eq('status', 'active')
        .gte('lease_end', new Date().toISOString().split('T')[0])
        .lte('lease_end', next60Days.toISOString().split('T')[0]);
      
      const leaseRenewals = renewals?.length || 0;

      // Get open maintenance requests
      const { data: openRequests } = await supabase
        .from('maintenance_requests')
        .select('id')
        .in('status', ['open', 'in_progress']);
      
      const maintenanceRequestsCount = openRequests?.length || 0;

      setDashboardData({
        monthlyRevenue,
        occupancyRate,
        maintenanceCosts,
        totalUnits,
        occupiedUnits,
        vacantUnits,
        leaseRenewals,
        maintenanceRequests: maintenanceRequestsCount
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  const checkUser = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUser(user);
      const { data: userTenant } = await supabase
        .from('user_tenants')
        .select('tenants(*)')
        .eq('user_id', user.id)
        .single();
      
      if (userTenant?.tenants) {
        setTenant(userTenant.tenants);
        await fetchDashboardData(userTenant.tenants.id);
      }
    }
    setLoading(false);
  }, [fetchDashboardData]);

  useEffect(() => {
    checkUser();
  }, [checkUser]);





  if (loading) return <div>Loading...</div>;
  if (!user) {
    window.location.href = '/';
    return null;
  }

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '40px' }}>
          <h2>Owner Portal - {tenant?.name || 'Property Owner'}</h2>
        </div>
        
        {/* Financial Overview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h3>Monthly Revenue</h3>
            <p style={{ fontSize: '24px', color: '#007bff', fontWeight: 'bold' }}>
              ${dashboardData.monthlyRevenue.toLocaleString()}
            </p>
            <p style={{ color: '#666' }}>Rent collected this month</p>
          </div>
          
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h3>Occupancy Rate</h3>
            <p style={{ fontSize: '24px', color: '#28a745', fontWeight: 'bold' }}>
              {dashboardData.occupancyRate}%
            </p>
            <p style={{ color: '#666' }}>
              {dashboardData.occupiedUnits} of {dashboardData.totalUnits} units occupied
            </p>
          </div>
          
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h3>Maintenance Costs</h3>
            <p style={{ fontSize: '24px', color: '#dc3545', fontWeight: 'bold' }}>
              ${dashboardData.maintenanceCosts.toLocaleString()}
            </p>
            <p style={{ color: '#666' }}>This month</p>
          </div>
        </div>

        {/* Reports & Analytics */}
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '30px' }}>
          <h3>Reports & Analytics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '20px' }}>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/reports'}
            >
              Financial Report
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/reports'}
            >
              Occupancy Report
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/reports'}
            >
              Maintenance Report
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/reports'}
            >
              Tenant Report
            </button>
          </div>
        </div>

        {/* Property Management */}
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
          <h3>Property Management</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '20px' }}>
            <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
              <h4>Vacant Units</h4>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{dashboardData.vacantUnits}</p>
              <button 
                className="btn btn-secondary" 
                style={{ fontSize: '12px' }}
                onClick={() => window.location.href = '/properties'}
              >
                View Details
              </button>
            </div>
            
            <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
              <h4>Lease Renewals</h4>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{dashboardData.leaseRenewals}</p>
              <button 
                className="btn btn-secondary" 
                style={{ fontSize: '12px' }}
                onClick={() => window.location.href = '/leasing'}
              >
                Review
              </button>
            </div>
            
            <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
              <h4>Maintenance Requests</h4>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{dashboardData.maintenanceRequests}</p>
              <button 
                className="btn btn-secondary" 
                style={{ fontSize: '12px' }}
                onClick={() => window.location.href = '/maintenance'}
              >
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OwnerDashboard;